// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  Timestamp
} from 'firebase/firestore';
import { deleteToken, getToken, onMessage } from 'firebase/messaging';
import {
  Wifi,
  WifiOff,
  Plus,
  Clock,
  Save,
  Trash2,
  X,
  Settings,
  User as UserIcon,
  MapPin,
  CheckCircle,
  AlertTriangle,
  ChevronLeft,
  Eye,
  Sun,
  Moon,
  Bell,
  BellOff
} from 'lucide-react';
import logo from './assets/logo-manobras.png';
import { BUILD_VERSION } from './buildVersion';
import { app, auth, db, getMessagingIfSupported } from './firebase';

const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
const ROLE_VIEWER = 0;
const ROLE_MANOBREIRO = 1;
const ROLE_OPERADOR = 2;
const ROLE_ADMIN = 3;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';
const AUTO_LOGOUT_HOURS = [0, 8, 16];
const BRAZIL_TIMEZONE = 'America/Sao_Paulo';

const getPartsInBrazilTime = (date = new Date()) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: BRAZIL_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute)
  };
};

const getBrazilDateString = (date = new Date()) => {
  const { year, month, day } = getPartsInBrazilTime(date);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const getCurrentDate = () => getBrazilDateString(new Date());
const getCurrentTimeObject = () => {
  const { hour, minute } = getPartsInBrazilTime(new Date());
  return {
    hour: String(hour).padStart(2, '0'),
    minute: String(minute).padStart(2, '0')
  };
};

// Retorna o limite máximo permitido (agora + 1 hora)
const getMaxAllowedDatetime = () => {
  const max = new Date(Date.now() + 60 * 60 * 1000);
  const { year, month, day, hour, minute } = getPartsInBrazilTime(max);
  return {
    dateStr: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    hour: String(hour).padStart(2, '0'),
    minute: String(minute).padStart(2, '0')
  };
};

const buildBrazilDateFromInput = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const time = timeStr || '00:00';
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  return new Date(Date.UTC(year, month - 1, day, hour, minute) + 3 * 60 * 60 * 1000);
};

// Cria um Timestamp do Firestore a partir das strings YYYY-MM-DD e HH:MM
const createTimestamp = (dateStr, timeStr) => {
  const dateObj = buildBrazilDateFromInput(dateStr, timeStr);
  if (!dateObj) return null;
  return Timestamp.fromDate(dateObj);
};

// Formata um Timestamp do Firestore para o padrão brasileiro DD/MM/AAAA HH:MM
const formatTimestampBR = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const formatter = new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRAZIL_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  return formatter.format(date);
};

const getTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const generateSearchTokens = (textOrObj) => {
  let rawText = '';
  if (typeof textOrObj === 'string') {
    rawText = textOrObj;
  } else if (textOrObj && typeof textOrObj === 'object') {
    rawText = textOrObj.registro || '';
  }

  if (!rawText) return [];

  const normalized = rawText
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const words = normalized.split(/[\s,._/\-\\]+/).filter((w) => w.length >= 1);
  const tokenSet = new Set();

  words.forEach((word) => {
    tokenSet.add(word);
    for (let i = 2; i <= word.length; i++) {
      tokenSet.add(word.substring(0, i));
    }
  });

  return Array.from(tokenSet);
};

function ControleScreen({ manobrasAbertas, manobrasFinalizadas, usuarios, bairros, darkMode, onView, goBack, onLoadMoreAbertas, onLoadMoreFinalizadas, onFilterSearch }) {
  const [searchRegistro, setSearchRegistro] = useState('');
  const [searchBairro, setSearchBairro] = useState('');

  useEffect(() => {
    if (typeof onFilterSearch === 'function') {
      onFilterSearch({ registro: searchRegistro, bairro: searchBairro });
    }
  }, [searchRegistro, searchBairro, onFilterSearch]);

  const emAndamento = [...(manobrasAbertas || [])]
    .sort((a, b) => {
      const timeA = a.inicio ? (a.inicio.toMillis ? a.inicio.toMillis() : 0) : 0;
      const timeB = b.inicio ? (b.inicio.toMillis ? b.inicio.toMillis() : 0) : 0;

      if (timeB !== timeA) return timeB - timeA; // ordem decrescente

      return getTimestampMillis(b.ultimaAtualizacao) - getTimestampMillis(a.ultimaAtualizacao);
    });
  const finalizadas = [...(manobrasFinalizadas || [])]
    .sort((a, b) => {
      const timeA = a.fim ? (a.fim.toMillis ? a.fim.toMillis() : 0) : 0;
      const timeB = b.fim ? (b.fim.toMillis ? b.fim.toMillis() : 0) : 0;

      if (timeB !== timeA) return timeB - timeA; // ordem decrescente

      return getTimestampMillis(b.ultimaAtualizacao) - getTimestampMillis(a.ultimaAtualizacao);
    });

  return (
    <div className={`flex flex-col h-screen overflow-hidden ${darkMode ? 'bg-black text-white' : 'bg-neutral-50 text-slate-900'}`}>
      <div className={`p-4 flex items-center justify-between border-b ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="flex items-center gap-3">
          <button onClick={goBack} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-neutral-900' : 'hover:bg-neutral-200'}`}><ChevronLeft size={22} /></button>
          <h2 className="text-xl font-bold">Controle de Manobras</h2>
        </div>
      </div>
      <div className={`p-4 border-b ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="flex flex-col md:flex-row gap-2">
          <input value={searchRegistro} onChange={(e) => setSearchRegistro(e.target.value)} placeholder="Buscar por registro..." className={`flex-1 p-2 border rounded-lg ${darkMode ? 'bg-neutral-900 border-slate-700 text-white' : 'bg-white border-slate-300'}`} />
          <select value={searchBairro} onChange={(e) => setSearchBairro(e.target.value)} className={`p-2 border rounded-lg ${darkMode ? 'bg-neutral-900 border-slate-700 text-white' : 'bg-white border-slate-300'}`}>
            <option value="">Todos os bairros</option>
            {[...(bairros || [])].sort((a, b) => a.nome.localeCompare(b.nome)).map((b) => <option key={b.id} value={b.nome}>{b.nome}</option>)}
          </select>
        </div>
      </div>
      <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
        <div className={`flex-1 min-h-0 border-r ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}>
          <div className={`p-3 font-bold ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>Em Andamento / Lembretes</div>
          <div className="h-full overflow-y-auto p-4 pb-14 space-y-3">
            {emAndamento.map((m) => <ManobraCard usuarios={usuarios} key={m.id} manobra={m} onClick={() => onView(m)} darkMode={darkMode} />)}
            {emAndamento.length >= 50 && (
              <button onClick={onLoadMoreAbertas} className={`w-full mt-2 py-2 rounded-lg font-semibold ${darkMode ? 'bg-neutral-900 text-slate-200 hover:bg-neutral-700' : 'bg-neutral-200 text-slate-700 hover:bg-neutral-300'}`}>Carregar mais</button>
            )}
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <div className={`p-3 font-bold ${darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>Finalizadas</div>
          <div className="h-full overflow-y-auto p-4 pb-14 space-y-3">
            {finalizadas.map((m) => <ManobraCard usuarios={usuarios} key={m.id} manobra={m} onClick={() => onView(m)} darkMode={darkMode} />)}
            {finalizadas.length >= 50 && (
              <button onClick={onLoadMoreFinalizadas} className={`w-full mt-2 py-2 rounded-lg font-semibold ${darkMode ? 'bg-neutral-900 text-slate-200 hover:bg-neutral-700' : 'bg-neutral-200 text-slate-700 hover:bg-neutral-300'}`}>Carregar mais</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [darkMode, setDarkMode] = useState(false);
  const [manobras, setManobras] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [bairros, setBairros] = useState([]);
  const [padroes, setPadroes] = useState([]);
  const [manobrasLimit, setManobrasLimit] = useState(50);
  const [manobrasFiltroAtivo, setManobrasFiltroAtivo] = useState(false);
  const [dashboardViewMode, setDashboardViewMode] = useState('abertas');
  const [controleManobrasAbertas, setControleManobrasAbertas] = useState([]);
  const [controleManobrasFinalizadas, setControleManobrasFinalizadas] = useState([]);
  const [controleLimitAbertas, setControleLimitAbertas] = useState(50);
  const [controleLimitFinalizadas, setControleLimitFinalizadas] = useState(50);
  const [route, setRoute] = useState('login');
  const [selectedManobra, setSelectedManobra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [syncMsg, setSyncMsg] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationToken, setNotificationToken] = useState('');
  const [notificationPermission, setNotificationPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');

  const initialNotificationLoadRef = useRef(false);
  const knownManobrasRef = useRef(new Map());
  const foregroundUnsubscribeRef = useRef(null);
  const rawSnapshotManobrasRef = useRef([]);
  const manobrasFiltroAtivoRef = useRef(false);
  const rawControleAbertasRef = useRef([]);
  const rawControleFinalizadasRef = useRef([]);
  const controleFiltroAtivoRef = useRef(false);

  const performAppLogout = useCallback((reason = '') => {
    setAppUser(null);
    setRoute('login');
    if (reason) {
      setErrorMsg(reason);
    } else {
      setErrorMsg('');
    }
  }, []);

  const handleViewManobra = (m, targetRoute) => {
    const dadosParaTela = { ...m };

    // Garante o mapeamento do campo acao caso abra algum registro de transição
    dadosParaTela.acao = dadosParaTela.acao || dadosParaTela.normal || 'Aberto';

    if (dadosParaTela.inicio) {
      const dataInicio = dadosParaTela.inicio.toDate ? dadosParaTela.inicio.toDate() : new Date(dadosParaTela.inicio);
      dadosParaTela.inicioDate = getBrazilDateString(dataInicio);
      dadosParaTela.inicioTime = new Intl.DateTimeFormat('pt-BR', {
        timeZone: BRAZIL_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(dataInicio);
    }

    if (dadosParaTela.fim) {
      const dataFim = dadosParaTela.fim.toDate ? dadosParaTela.fim.toDate() : new Date(dadosParaTela.fim);
      dadosParaTela.fimDate = getBrazilDateString(dataFim);
      dadosParaTela.fimTime = new Intl.DateTimeFormat('pt-BR', {
        timeZone: BRAZIL_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(dataFim);
    }

    setSelectedManobra(dadosParaTela);
    setRoute(targetRoute);
  };

  const triggerBackendSync = useCallback(() => {
    fetch('https://manobra-saaevr-backend.onrender.com/sync', { method: 'POST' })
      .then(res => res.json())
      .then(data => console.log('Notificações sincronizadas:', data))
      .catch(err => console.error('Erro ao acionar notificações do backend:', err));
  }, []);

  const baseCollection = useCallback((name) => collection(db, 'artifacts', appId, 'public', 'data', name), []);

  const showBrowserNotification = useCallback((title, body, data = {}) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    try {
      const notification = new Notification(title, {
        body,
        icon: '/logo192.png',
        data
      });
      notification.onclick = () => {
        window.focus();
      };
    } catch (err) {
      console.warn('Falha ao exibir notificação local:', err);
    }
  }, []);

  const persistNotificationToken = useCallback(async (token, permissionOverride) => {
    const permissionValue = permissionOverride || (typeof Notification !== 'undefined' ? Notification.permission : 'default');
    const userKey = appUser?.login || firebaseUser?.uid || 'anon';
    const tokenId = `${userKey}_${token}`.replace(/[^a-zA-Z0-9_-]/g, '_');

    await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'notificationTokens', tokenId), {
      token,
      userLogin: appUser?.login || '',
      userName: appUser?.nome || '',
      firebaseUid: firebaseUser?.uid || '',
      platform: 'web',
      enabled: permissionValue === 'granted',
      permission: permissionValue,
      userAgent: navigator.userAgent,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString()
    }, { merge: true });
  }, [appUser, firebaseUser]);

  const registerForegroundListener = useCallback(async () => {
    if (foregroundUnsubscribeRef.current) {
      foregroundUnsubscribeRef.current();
      foregroundUnsubscribeRef.current = null;
    }

    const messaging = await getMessagingIfSupported();
    if (!messaging) return;

    foregroundUnsubscribeRef.current = onMessage(messaging, (payload) => {
      const title = payload?.notification?.title || 'Manobras';
      const body = payload?.notification?.body || 'Nova notificação recebida.';
      showBrowserNotification(title, body, payload?.data || {});
    });
  }, [showBrowserNotification]);

  const enablePushNotifications = useCallback(async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // --- FLUXO ANDROID (APK) ---

        // 1. Pede permissão nativa
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== 'granted') {
          throw new Error('Permissão de notificação negada no dispositivo.');
        }

        // 2. Registra o ouvinte para receber o token do Firebase
        await PushNotifications.addListener('registration', async (tokenInfo) => {
          const token = tokenInfo.value;
          await persistNotificationToken(token, 'granted');
          setNotificationToken(token);
          setNotificationsEnabled(true);
          setSyncMsg('Notificações no App ativadas com sucesso.');
          setTimeout(() => setSyncMsg(''), 4000);
        });

        // 3. Ouvinte de erro de registro
        await PushNotifications.addListener('registrationError', (err) => {
          console.error('Erro de registro Push:', err);
        });

        // 4. Ouvinte de notificação recebida com o app aberto (Foreground)
        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('Notificação recebida:', notification);
          // O Capacitor já mostra o alerta por padrão dependendo do presentationOptions,
          // mas você pode chamar o showBrowserNotification se quiser manter seu padrão visual.
          showBrowserNotification(
            notification.title || 'Manobras',
            notification.body || 'Nova notificação recebida.',
            notification.data
          );
        });

        // 5. Inicia o registro no Firebase (Isto dispara o evento 'registration' acima)
        await PushNotifications.register();

      } else {
        // --- FLUXO WEB (Navegador) - Seu código original mantido ---
        if (!('Notification' in window)) throw new Error('Este navegador não suporta notificações.');
        if (!VAPID_KEY) throw new Error('REACT_APP_FIREBASE_VAPID_KEY não configurada.');

        const messaging = await getMessagingIfSupported();
        if (!messaging) throw new Error('Firebase Messaging não suportado neste contexto.');

        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
        if (permission !== 'granted') {
          setNotificationsEnabled(false);
          setNotificationToken('');
          setSyncMsg('Permissão de notificação não concedida.');
          return;
        }

        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });

        if (!token) throw new Error('Nenhum token FCM foi gerado.');

        await persistNotificationToken(token, permission);
        await registerForegroundListener();

        setNotificationToken(token);
        setNotificationsEnabled(true);
        setSyncMsg('Notificações web ativadas com sucesso.');
        setTimeout(() => setSyncMsg(''), 4000);
      }
    } catch (err) {
      console.error('Erro ao ativar notificações:', err);
      setErrorMsg('Erro ao ativar notificações: ' + err.message);
      setNotificationsEnabled(false);
    }
  }, [persistNotificationToken, registerForegroundListener, showBrowserNotification]);

  const disablePushNotifications = useCallback(async () => {
    try {
      const messaging = await getMessagingIfSupported();
      if (messaging && notificationToken) {
        await deleteToken(messaging).catch((err) => {
          console.warn('Não foi possível revogar o token atual:', err);
        });
      }

      if (notificationToken) {
        const userKey = appUser?.login || firebaseUser?.uid || 'anon';
        const tokenId = `${userKey}_${notificationToken}`.replace(/[^a-zA-Z0-9_-]/g, '_');
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'notificationTokens', tokenId), {
          token: notificationToken,
          userLogin: appUser?.login || '',
          userName: appUser?.nome || '',
          firebaseUid: firebaseUser?.uid || '',
          platform: 'web',
          enabled: false,
          permission: typeof Notification !== 'undefined' ? Notification.permission : 'default',
          updatedAt: new Date().toISOString()
        }, { merge: true });
      }

      if (foregroundUnsubscribeRef.current) {
        foregroundUnsubscribeRef.current();
        foregroundUnsubscribeRef.current = null;
      }

      setNotificationsEnabled(false);
      setNotificationToken('');
      setSyncMsg('Notificações web desativadas neste navegador.');
      setTimeout(() => setSyncMsg(''), 4000);
    } catch (err) {
      console.error('Erro ao desativar notificações:', err);
      setErrorMsg(`Erro ao desativar notificações: ${err.message}`);
    }
  }, [appUser, firebaseUser, notificationToken]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setSyncMsg('Conexão restabelecida. Sincronizando alterações pendentes...');
      triggerBackendSync();
      setTimeout(() => setSyncMsg(''), 4000);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setSyncMsg('Modo offline ativo. As alterações serão salvas localmente e enviadas quando a conexão voltar.');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error('Erro na autenticação Firebase:', err);
        setErrorMsg('Erro de autenticação de segurança. Verifique sua conexão.');
      }
    };
    initAuth();

    const unsubscribeAuth = onAuthStateChanged(auth, (u) => {
      setFirebaseUser(u || null);
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!appUser) return undefined;

    let timeoutId;

    const scheduleAutoLogout = () => {
      const now = new Date();
      let nextLogoutTime = null;

      for (const hour of AUTO_LOGOUT_HOURS) {
        const candidate = new Date(now);
        candidate.setHours(hour, 0, 0, 0);

        if (candidate.getTime() === now.getTime()) {
          nextLogoutTime = new Date(now.getTime() + 1000);
          break;
        }

        if (candidate.getTime() > now.getTime()) {
          nextLogoutTime = candidate;
          break;
        }
      }

      if (!nextLogoutTime) {
        const nextDay = new Date(now);
        nextDay.setDate(nextDay.getDate() + 1);
        nextDay.setHours(0, 0, 0, 0);
        nextLogoutTime = nextDay;
      }

      const delay = Math.max(1000, nextLogoutTime.getTime() - now.getTime());
      timeoutId = window.setTimeout(() => {
        performAppLogout('Sessão encerrada automaticamente para troca de turno.');
        scheduleAutoLogout();
      }, delay);
    };

    scheduleAutoLogout();

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [appUser, performAppLogout]);

  useEffect(() => {
    if (!firebaseUser) return;

    const unsubUsu = onSnapshot(baseCollection('usuarios'), (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsuarios(data);
      if (data.length === 0) seedInitialData([], [], []);
    }, (err) => console.error('Erro na escuta de usuários:', err));

    const unsubBai = onSnapshot(baseCollection('bairros'), (snap) => {
      setBairros(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('Erro na escuta de bairros:', err));

    const unsubPad = onSnapshot(baseCollection('padroes'), (snap) => {
      setPadroes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('Erro na escuta de padrões de manobra:', err));

    const statusList = dashboardViewMode === 'abertas'
      ? ['Lembrete', 'Em Progresso']
      : dashboardViewMode === 'finalizadas'
        ? ['Finalizada']
        : ['Cancelada'];

    const baseManobrasQuery = dashboardViewMode === 'finalizadas'
      ? query(baseCollection('manobras'), where('status', 'in', statusList), orderBy('fim', 'desc'), limit(manobrasLimit))
      : query(baseCollection('manobras'), where('status', 'in', statusList), orderBy('inicio', 'desc'), limit(manobrasLimit));

    const unsubMan = onSnapshot(baseManobrasQuery, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rawSnapshotManobrasRef.current = data;
      if (!manobrasFiltroAtivoRef.current) {
        setManobras(data);
      }
      const hasPendingWrites = snap.metadata.hasPendingWrites;
      if (hasPendingWrites) {
        setSyncMsg('Alterações pendentes salvas localmente. Aguardando sincronização com o servidor...');
      } else if (navigator.onLine) {
        setSyncMsg('');
      }
      setLoading(false);
    }, (err) => {
      console.error('Erro na escuta de manobras:', err);
      setErrorMsg('Erro de permissão ou conexão ao carregar registros.');
      setLoading(false);
    });

    return () => {
      unsubUsu();
      unsubBai();
      unsubPad();
      unsubMan();
    };
  }, [baseCollection, dashboardViewMode, firebaseUser, manobrasLimit]);

  useEffect(() => {
    if (!firebaseUser || route !== 'controle') return;
    const baseRef = baseCollection('manobras');

    const qAbertas = query(baseRef, where('status', 'in', ['Lembrete', 'Em Progresso']), orderBy('inicio', 'desc'), limit(controleLimitAbertas));
    const qFinalizadas = query(baseRef, where('status', '==', 'Finalizada'), orderBy('fim', 'desc'), limit(controleLimitFinalizadas));

    const unsubAbertas = onSnapshot(qAbertas, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rawControleAbertasRef.current = data;
      if (!controleFiltroAtivoRef.current) {
        setControleManobrasAbertas(data);
      }
    }, (err) => {
      console.error('Erro ao carregar controle abertas:', err);
      setErrorMsg('Erro ao carregar Em Andamento / Lembretes no Controle.');
    });

    const unsubFinalizadas = onSnapshot(qFinalizadas, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rawControleFinalizadasRef.current = data;
      if (!controleFiltroAtivoRef.current) {
        setControleManobrasFinalizadas(data);
      }
    }, (err) => {
      console.error('Erro ao carregar controle finalizadas:', err);
      setErrorMsg('Erro ao carregar Finalizadas no Controle.');
    });

    return () => {
      unsubAbertas();
      unsubFinalizadas();
    };
  }, [baseCollection, controleLimitAbertas, controleLimitFinalizadas, firebaseUser, route]);

  useEffect(() => {
    if (!firebaseUser) return;

    const q = query(baseCollection('manobras'), orderBy('ultimaAtualizacao', 'desc'), limit(200));
    const unsub = onSnapshot(q, (snap) => {
      const currentMap = new Map();

      snap.docs.forEach((d) => {
        const data = { id: d.id, ...d.data() };
        currentMap.set(d.id, data);
      });

      if (!initialNotificationLoadRef.current) {
        knownManobrasRef.current = currentMap;
        initialNotificationLoadRef.current = true;
        return;
      }

      currentMap.forEach((curr, id) => {
        const prev = knownManobrasRef.current.get(id);
        if (!prev) {
          showBrowserNotification('Nova manobra criada', `${curr.registro || 'Registro sem nome'} • ${curr.local || 'Local não informado'}`, { type: 'created', manobraId: id });
          return;
        }

        if (normalizeText(prev.status) !== 'finalizada' && normalizeText(curr.status) === 'finalizada') {
          showBrowserNotification('Manobra concluída', `${curr.registro || 'Registro sem nome'} foi finalizada.`, { type: 'finalized', manobraId: id });
        }
      });

      knownManobrasRef.current = currentMap;
    }, (err) => {
      console.error('Erro ao observar mudanças para notificações locais:', err);
    });

    return () => unsub();
  }, [baseCollection, firebaseUser, showBrowserNotification]);

  useEffect(() => {
    setNotificationPermission(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  }, []);

  useEffect(() => {
    if (!firebaseUser) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    enablePushNotifications().catch(() => { });
  }, [enablePushNotifications, firebaseUser, appUser]);

  useEffect(() => () => {
    if (foregroundUnsubscribeRef.current) foregroundUnsubscribeRef.current();
  }, []);

  const handleLogin = (login, senha) => {
    // Remove todos os espaços em branco do login digitado e passa para minúsculo
    const loginLimpo = login.replace(/\s+/g, '').toLowerCase();

    const user = usuarios.find((u) => {
      // Limpa também o login que vem do banco (por segurança)
      const dbLogin = u.login.replace(/\s+/g, '').toLowerCase();

      // Compara os logins sem espaços, mas exige que a senha seja EXATAMENTE igual
      return dbLogin === loginLimpo && u.senha === senha;
    });

    if (user) {
      setAppUser(user);
      setRoute('dashboard');
      setErrorMsg('');
    } else {
      setErrorMsg('Usuário ou senha incorretos.');
    }
  };

  const handleLogout = () => {
    performAppLogout();
  };

  const handleCarregarMaisManobras = () => setManobrasLimit((prev) => prev + 50);

  const handleBuscarManobrasFiltradas = async ({ registro, bairro, viewMode }) => {
    if (!firebaseUser) return;
    try {
      const hasFilter = !!(registro || bairro);
      manobrasFiltroAtivoRef.current = hasFilter;
      setManobrasFiltroAtivo(hasFilter);
      if (!hasFilter) {
        setManobras(rawSnapshotManobrasRef.current);
        setErrorMsg('');
        return;
      }
      const baseRef = baseCollection('manobras');
      const statusList = viewMode === 'abertas'
        ? ['Lembrete', 'Em Progresso']
        : viewMode === 'finalizadas'
          ? ['Finalizada']
          : ['Cancelada'];

      const dateField = viewMode === 'finalizadas' ? 'fim' : 'inicio';
      const dataMap = new Map();

      if (bairro) {
        const qBairro = query(baseRef, where('local', '==', bairro), orderBy(dateField, 'desc'), limit(150));
        const snap = await getDocs(qBairro);
        snap.docs.forEach((d) => dataMap.set(d.id, { id: d.id, ...d.data() }));
      }

      if (registro) {
        const regTrim = registro.trim();
        const termNorm = regTrim.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (termNorm) {
          const qTokens = query(baseRef, where('searchTokens', 'array-contains', termNorm), limit(100));
          const snapTokens = await getDocs(qTokens);
          snapTokens.docs.forEach((d) => dataMap.set(d.id, { id: d.id, ...d.data() }));

          const qReg = query(baseRef, where('registro', '>=', regTrim), where('registro', '<=', regTrim + '\uf8ff'), limit(50));
          const snapReg = await getDocs(qReg);
          snapReg.docs.forEach((d) => dataMap.set(d.id, { id: d.id, ...d.data() }));
        }
      }

      if (!bairro && !registro) {
        const qRecentes = query(baseRef, orderBy(dateField, 'desc'), limit(150));
        const snapRec = await getDocs(qRecentes);
        snapRec.docs.forEach((d) => dataMap.set(d.id, { id: d.id, ...d.data() }));
      } else if (!bairro && registro && dataMap.size < 10) {
        const qRecentes = query(baseRef, orderBy(dateField, 'desc'), limit(100));
        const snapRec = await getDocs(qRecentes);
        snapRec.docs.forEach((d) => dataMap.set(d.id, { id: d.id, ...d.data() }));
      }

      let data = Array.from(dataMap.values());

      data = data.filter((m) => statusList.includes(m.status));

      if (bairro) {
        data = data.filter((m) => m.local === bairro);
      }

      if (registro) {
        const regLower = registro.toLowerCase();
        data = data.filter((m) => (m.registro || '').toLowerCase().includes(regLower));
      }

      data.sort((a, b) => {
        if (viewMode === 'finalizadas') {
          return getTimestampMillis(b.fim) - getTimestampMillis(a.fim);
        }
        return getTimestampMillis(b.inicio) - getTimestampMillis(a.inicio);
      });

      setManobras(data);
      setErrorMsg('');
    } catch (err) {
      console.error('Erro ao buscar manobras filtradas:', err);
      setErrorMsg('Erro ao buscar manobras filtradas.');
    }
  };

  const handleBuscarControleFiltradas = async ({ registro, bairro }) => {
    if (!firebaseUser) return;
    try {
      const hasFilter = !!(registro || bairro);
      controleFiltroAtivoRef.current = hasFilter;
      if (!hasFilter) {
        setControleManobrasAbertas(rawControleAbertasRef.current);
        setControleManobrasFinalizadas(rawControleFinalizadasRef.current);
        setErrorMsg('');
        return;
      }
      const baseRef = baseCollection('manobras');
      const dataMap = new Map();

      if (bairro) {
        const qBairro = query(baseRef, where('local', '==', bairro), orderBy('inicio', 'desc'), limit(150));
        const snap = await getDocs(qBairro);
        snap.docs.forEach((d) => dataMap.set(d.id, { id: d.id, ...d.data() }));
      }

      if (registro) {
        const regTrim = registro.trim();
        const termNorm = regTrim.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (termNorm) {
          const qTokens = query(baseRef, where('searchTokens', 'array-contains', termNorm), limit(100));
          const snapTokens = await getDocs(qTokens);
          snapTokens.docs.forEach((d) => dataMap.set(d.id, { id: d.id, ...d.data() }));

          const qReg = query(baseRef, where('registro', '>=', regTrim), where('registro', '<=', regTrim + '\uf8ff'), limit(50));
          const snapReg = await getDocs(qReg);
          snapReg.docs.forEach((d) => dataMap.set(d.id, { id: d.id, ...d.data() }));
        }
      }

      if (!bairro && !registro) {
        const qRecentes = query(baseRef, orderBy('inicio', 'desc'), limit(150));
        const snapRec = await getDocs(qRecentes);
        snapRec.docs.forEach((d) => dataMap.set(d.id, { id: d.id, ...d.data() }));
      } else if (!bairro && registro && dataMap.size < 10) {
        const qRecentes = query(baseRef, orderBy('inicio', 'desc'), limit(100));
        const snapRec = await getDocs(qRecentes);
        snapRec.docs.forEach((d) => dataMap.set(d.id, { id: d.id, ...d.data() }));
      }

      let allData = Array.from(dataMap.values());

      if (bairro) {
        allData = allData.filter((m) => m.local === bairro);
      }

      if (registro) {
        const regLower = registro.toLowerCase();
        allData = allData.filter((m) => (m.registro || '').toLowerCase().includes(regLower));
      }

      let dataAbertas = allData.filter((m) => ['Lembrete', 'Em Progresso'].includes(m.status));
      let dataFinalizadas = allData.filter((m) => m.status === 'Finalizada');

      dataAbertas.sort((a, b) => getTimestampMillis(b.inicio) - getTimestampMillis(a.inicio));
      dataFinalizadas.sort((a, b) => getTimestampMillis(b.fim) - getTimestampMillis(a.fim));

      setControleManobrasAbertas(dataAbertas);
      setControleManobrasFinalizadas(dataFinalizadas);
      setErrorMsg('');
    } catch (err) {
      console.error('Erro ao buscar no controle de manobras:', err);
      setErrorMsg('Erro ao buscar registros no Controle.');
    }
  };

  const saveManobra = async (dados) => {
    if (!firebaseUser) {
      setErrorMsg('Não autenticado. Aguarde sincronização.');
      return;
    }
    const isNew = !dados.id;
    const now = new Date();
    const tsStr = `${now.toLocaleDateString('pt-BR')} ${now.toLocaleTimeString('pt-BR')}`;
    const updatedAt = Timestamp.fromDate(now);
    const newHistoryEntry = `${tsStr} - ${appUser.login}`;
    const updatedHistory = dados.historicoEdicao ? `${dados.historicoEdicao} / ${newHistoryEntry}` : newHistoryEntry;

    // ... dentro de saveManobra ...
    let calcStatus = 'Lembrete';
    if (dados.status === 'Cancelada') calcStatus = 'Cancelada';
    else if (dados.fimDate || dados.fim) calcStatus = 'Finalizada';
    else if (dados.inicioDate || dados.inicio) calcStatus = 'Em Progresso';

    // 1. Captura a ação (que virá do estado do seu formulário)
    const valorAcao = dados.acao || dados.normal || 'Aberto';

    // 2. Calcula automaticamente o oposto para o statusnormal
    const valorStatusNormal = valorAcao === 'Aberto' ? 'Fechado' : 'Aberto';

    const payload = {
      ...dados,
      status: calcStatus,
      historicoEdicao: updatedHistory,
      usuarioUltimaAlteracao: appUser.login,
      ultimaAtualizacao: updatedAt,
      inicio: createTimestamp(dados.inicioDate, dados.inicioTime),
      fim: createTimestamp(dados.fimDate, dados.fimTime),

      // Adicionamos os novos campos estruturados
      acao: valorAcao,
      statusnormal: valorStatusNormal,
      searchTokens: generateSearchTokens(dados)
    };

    // Removemos os campos de string antigos e o 'normal' para manter o banco limpo
    delete payload.inicioDate;
    delete payload.inicioTime;
    delete payload.fimDate;
    delete payload.fimTime;
    delete payload.normal; // <-- Garante que o campo antigo não seja mais gravado

    const savePromise = isNew
      ? addDoc(baseCollection('manobras'), payload)
      : (() => {
        const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'manobras', dados.id);
        const updatePayload = { ...payload };
        delete updatePayload.id;
        return updateDoc(docRef, updatePayload);
      })();

    setSelectedManobra(null);
    setRoute('dashboard');
    setSyncMsg(navigator.onLine ? 'Salvando manobra...' : 'Manobra salva localmente no modo offline. Será sincronizada quando a conexão voltar.');

    try {
      await savePromise;
      setSyncMsg(navigator.onLine ? 'Manobra salva com sucesso.' : 'Manobra salva localmente no modo offline. Será sincronizada quando a conexão voltar.');

      if (navigator.onLine) {
        triggerBackendSync();
      }

    } catch (e) {
      setErrorMsg('Erro ao salvar manobra. ' + e.message);
    }
  };

  const deleteManobra = async (id) => {
    if (!firebaseUser) return;
    const savePromise = (() => {
      const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'manobras', id);
      return updateDoc(docRef, { status: 'Cancelada', historicoEdicao: `Cancelada por ${appUser.login} em ${getCurrentDate()}` });
    })();

    setSelectedManobra(null);
    setRoute('dashboard');
    setSyncMsg(navigator.onLine ? 'Salvando cancelamento...' : 'Cancelamento salvo localmente no modo offline. Será sincronizado quando a conexão voltar.');

    try {
      await savePromise;
      setSyncMsg(navigator.onLine ? 'Cancelamento salvo com sucesso.' : 'Cancelamento salvo localmente no modo offline. Será sincronizado quando a conexão voltar.');

      if (navigator.onLine) {
        triggerBackendSync();
      }

    } catch (e) {
      setErrorMsg('Erro ao cancelar. ' + e.message);
    }
  };

  if (loading && route !== 'login' && manobras.length === 0) {
    return <div className={`flex items-center justify-center h-screen ${darkMode ? 'bg-black text-slate-400' : 'bg-neutral-50 text-slate-500'}`}>Carregando base de dados...</div>;
  }

  return (
    <div className={`min-h-screen font-sans transition-colors duration-300 ${darkMode ? 'bg-black text-slate-100' : 'bg-neutral-100 text-slate-800'}`}>
      {appUser && (
        <header className={`${darkMode ? 'bg-neutral-900 border-b border-slate-700' : 'bg-blue-600'} text-white p-4 shadow-md sticky top-0 z-50`}>
          <div className="max-w-full mx-auto flex justify-between items-center">
            <div className="flex items-center gap-2">
              {route !== 'dashboard' && (
                <button onClick={() => setRoute('dashboard')} className={`p-1 rounded mr-2 ${darkMode ? 'hover:bg-neutral-700' : 'hover:bg-blue-700'}`}>
                  <ChevronLeft size={24} />
                </button>
              )}
              <div className="flex items-center gap-2">
                <img src={logo} alt="Manobras" className="h-8 w-auto object-contain" />
                {appUser.role === ROLE_VIEWER && (
                  <span className={`${darkMode ? 'bg-neutral-700 text-slate-300' : 'bg-blue-800 text-blue-200'} text-xs px-2 py-0.5 rounded-full font-normal flex items-center gap-1`}>
                    <Eye size={12} /> Somente Leitura
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'hover:bg-neutral-700 text-yellow-400' : 'hover:bg-blue-700 text-blue-100'}`}
                title={darkMode ? 'Ativar Modo Claro' : 'Ativar Modo Escuro'}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              <button
                onClick={notificationsEnabled ? disablePushNotifications : enablePushNotifications}
                className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'hover:bg-neutral-700' : 'hover:bg-blue-700'} ${notificationsEnabled ? 'text-emerald-300' : 'text-white'}`}
                title={notificationsEnabled ? 'Desativar notificações web' : 'Ativar notificações web'}
              >
                {notificationsEnabled ? <Bell size={20} /> : <BellOff size={20} />}
              </button>

              <div className="flex items-center gap-1" title={isOnline ? 'Sinal Online' : 'Sinal Offline'}>
                {isOnline ? <Wifi size={16} className="text-emerald-400" /> : <WifiOff size={16} className="text-red-400" />}
              </div>

              <div className="hidden sm:flex items-center gap-1">
                <UserIcon size={16} />
                <span className="font-semibold">{appUser.nome}</span>
              </div>

              {(appUser.role === ROLE_ADMIN || appUser.role === ROLE_OPERADOR) && (
                <button onClick={() => setRoute('admin')} className={`p-1 rounded ${darkMode ? 'hover:bg-neutral-700' : 'hover:bg-blue-700'}`} title="Painel Admin">
                  <Settings size={20} />
                </button>
              )}

              <button onClick={handleLogout} className={`p-1 rounded px-2 text-xs font-semibold ${darkMode ? 'bg-neutral-700 hover:bg-neutral-600' : 'bg-blue-700 hover:bg-blue-800'}`}>
                Sair
              </button>
            </div>
          </div>
        </header>
      )}

      <main className="max-w-full mx-auto p-4">
        {errorMsg && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 flex justify-between">
            <span>{errorMsg}</span>
            <button onClick={() => setErrorMsg('')}><X size={18} /></button>
          </div>
        )}

        {syncMsg && (
          <div className={`${darkMode ? 'bg-amber-950/30 border-amber-900/60 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'} border px-4 py-3 rounded relative mb-4 flex justify-between`}>
            <span>{syncMsg}</span>
            <button onClick={() => setSyncMsg('')}><X size={18} /></button>
          </div>
        )}

        {notificationPermission === 'denied' && (
          <div className={`${darkMode ? 'bg-red-950/30 border-red-900/60 text-red-200' : 'bg-red-50 border-red-200 text-red-800'} border px-4 py-3 rounded relative mb-4`}>
            As notificações estão bloqueadas no navegador. Reative manualmente nas permissões do site para usar o FCM web.
          </div>
        )}

        {route === 'login' && <LoginScreen onLogin={handleLogin} darkMode={darkMode} setDarkMode={setDarkMode} />}

        {route === 'controle' && (
          <ControleScreen
            manobrasAbertas={controleManobrasAbertas}
            manobrasFinalizadas={controleManobrasFinalizadas}
            usuarios={usuarios}
            bairros={bairros}
            darkMode={darkMode}
            onView={(m) => handleViewManobra(m, 'details')}
            goBack={() => setRoute('dashboard')}
            onLoadMoreAbertas={() => setControleLimitAbertas((prev) => prev + 50)}
            onLoadMoreFinalizadas={() => setControleLimitFinalizadas((prev) => prev + 50)}
            onFilterSearch={handleBuscarControleFiltradas}
          />
        )}

        {route === 'dashboard' && (
          <Dashboard
            usuarios={usuarios}
            bairros={bairros}
            manobras={manobras}
            appUser={appUser}
            darkMode={darkMode}
            onControle={() => setRoute('controle')}
            onNew={() => { setSelectedManobra(null); setRoute('form'); }}
            onView={(m) => handleViewManobra(m, 'details')}
            onLoadMore={handleCarregarMaisManobras}
            onFilterSearch={handleBuscarManobrasFiltradas}
            viewMode={dashboardViewMode}
            onViewModeChange={setDashboardViewMode}
            manobrasLimit={manobrasLimit}
          />
        )}

        {route === 'form' && (
          <ManobraForm
            manobra={selectedManobra}
            appUser={appUser}
            bairros={bairros}
            padroes={padroes}
            usuarios={usuarios}
            darkMode={darkMode}
            onSave={saveManobra}
            onCancel={() => setRoute(selectedManobra ? 'details' : 'dashboard')}
          />
        )}

        {route === 'details' && (
          <ManobraDetails
            manobra={selectedManobra}
            appUser={appUser}
            usuarios={usuarios}
            darkMode={darkMode}
            onEdit={() => setRoute('form')}
            onDelete={() => deleteManobra(selectedManobra.id)}
            onSave={saveManobra}
          />
        )}

        {route === 'admin' && (appUser?.role === ROLE_ADMIN || appUser?.role === ROLE_OPERADOR) && (
          <AdminPanel appUser={appUser} usuarios={usuarios} bairros={bairros} padroes={padroes} db={db} appId={appId} darkMode={darkMode} />
        )}
      </main>

      {appUser && route !== 'login' && (
        <div className={`fixed bottom-2 left-3 text-[11px] z-50 pointer-events-none font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Logado como: {appUser.nome}
        </div>
      )}

      <div className={`fixed bottom-2 right-3 text-[11px] z-50 pointer-events-none ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        v{BUILD_VERSION}
      </div>

    </div>
  );
}

function LoginScreen({ onLogin, darkMode, setDarkMode }) {
  const [login, setLogin] = useState('');
  const [senha, setSenha] = useState('');

  return (
    <div className="flex items-center justify-center min-h-[80vh]">
      <div className={`p-8 rounded-xl shadow-lg w-full max-w-sm transition-colors duration-300 ${darkMode ? 'bg-neutral-900 border border-slate-700' : 'bg-white'}`}>
        <div className="flex justify-end mb-2">
          <button onClick={() => setDarkMode(!darkMode)} className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'hover:bg-neutral-700 text-yellow-400' : 'hover:bg-neutral-100 text-slate-600'}`}>
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>

        <div className="text-center mb-8">
          <img src={logo} alt="Manobras" className="mx-auto mb-3 h-20 w-auto object-contain" />
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>Manobras</h2>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Gestão de Manobras</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Usuário</label>
            <input type="text" value={login} onChange={(e) => setLogin(e.target.value)} className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-colors ${darkMode ? 'bg-neutral-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`} placeholder="Digite seu login" />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Senha</label>
            <input type="password" value={senha} onChange={(e) => setSenha(e.target.value)} className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-colors ${darkMode ? 'bg-neutral-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300'}`} placeholder="••••••••" />
          </div>
          <button onClick={() => onLogin(login, senha)} className="w-full bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700 transition mt-4">Entrar</button>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ manobras, appUser, usuarios, bairros, onNew, onView, onControle, darkMode, onLoadMore, onFilterSearch, viewMode, onViewModeChange, manobrasLimit }) {
  const [searchRegistro, setSearchRegistro] = useState('');
  const [searchBairro, setSearchBairro] = useState('');

  const filteredManobras = [...(manobras || [])].filter((m) => {
    const st = m.status || '';
    return viewMode === 'abertas' ? (st === 'Lembrete' || st === 'Em Progresso') : viewMode === 'finalizadas' ? st === 'Finalizada' : st === 'Cancelada';
  });

  useEffect(() => {
    if (typeof onFilterSearch === 'function') onFilterSearch({ registro: searchRegistro, bairro: searchBairro, viewMode });
  }, [searchRegistro, searchBairro, viewMode, onFilterSearch]);

  // Lógica de ordenação atualizada para usar os novos Timestamps
  const sortedManobras = [...filteredManobras].sort((a, b) => {
    if (viewMode === 'finalizadas') {
      // Extrai os milissegundos dos campos 'fim', com fallback para 0 se não existir
      const timeA = a.fim ? (a.fim.toMillis ? a.fim.toMillis() : new Date(a.fim).getTime()) : 0;
      const timeB = b.fim ? (b.fim.toMillis ? b.fim.toMillis() : new Date(b.fim).getTime()) : 0;
      return timeB - timeA; // Ordem decrescente (mais recentes primeiro)
    }

    // Extrai os milissegundos dos campos 'inicio', com fallback para 0 se não existir
    const timeA = a.inicio ? (a.inicio.toMillis ? a.inicio.toMillis() : new Date(a.inicio).getTime()) : 0;
    const timeB = b.inicio ? (b.inicio.toMillis ? b.inicio.toMillis() : new Date(b.inicio).getTime()) : 0;
    return timeB - timeA; // Ordem decrescente (mais recentes primeiro)
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Manobras</h2>
        <div className="flex gap-2 flex-wrap">
          <button onClick={onControle} className={`px-4 py-2 rounded-lg flex items-center gap-2 shadow transition-colors font-bold ${darkMode ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}>
            <Eye size={20} /> <span className="hidden sm:inline">Controle de Manobras</span>
          </button>
          {appUser.role !== ROLE_VIEWER && (
            <button onClick={onNew} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 shadow transition-colors">
              <Plus size={20} /> <span className="hidden sm:inline">Nova Manobra</span>
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => onViewModeChange('abertas')} className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'abertas' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-neutral-900 text-slate-300' : 'bg-neutral-200 text-slate-700')}`}>Lembretes / Em Progresso</button>
          <button onClick={() => onViewModeChange('finalizadas')} className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'finalizadas' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-neutral-900 text-slate-300' : 'bg-neutral-200 text-slate-700')}`}>Finalizadas</button>
          <button onClick={() => onViewModeChange('canceladas')} className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'canceladas' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-neutral-900 text-slate-300' : 'bg-neutral-200 text-slate-700')}`}>Canceladas</button>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input value={searchRegistro} onChange={(e) => setSearchRegistro(e.target.value)} placeholder="Buscar por registro..." className={`flex-1 p-2 border rounded-lg ${darkMode ? 'bg-neutral-900 border-slate-700 text-white' : 'bg-white border-slate-300'}`} />
          <select value={searchBairro} onChange={(e) => setSearchBairro(e.target.value)} className={`p-2 border rounded-lg ${darkMode ? 'bg-neutral-900 border-slate-700 text-white' : 'bg-white border-slate-300'}`}>
            <option value="">Todos os bairros</option>
            {[...(bairros || [])].sort((a, b) => a.nome.localeCompare(b.nome)).map((b) => <option key={b.id} value={b.nome}>{b.nome}</option>)}
          </select>
        </div>
      </div>

      <div className="grid gap-4">
        {sortedManobras.length === 0 ? (
          <div className={`text-center py-10 rounded-xl border border-dashed ${darkMode ? 'text-slate-400 bg-neutral-900/50 border-slate-700' : 'text-slate-500 bg-white border-slate-300'}`}>
            Nenhuma manobra registrada.
          </div>
        ) : (
          <>
            {sortedManobras.map((m) => <ManobraCard usuarios={usuarios} key={m.id} manobra={m} onClick={() => onView(m)} darkMode={darkMode} />)}
            {!searchRegistro && !searchBairro && typeof onLoadMore === 'function' && manobras.length >= manobrasLimit && (
              <button onClick={onLoadMore} className={`w-full py-2 rounded-lg font-semibold ${darkMode ? 'bg-neutral-900 text-slate-200 hover:bg-neutral-700' : 'bg-neutral-200 text-slate-700 hover:bg-neutral-300'}`}>Carregar mais</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ManobraCard({ manobra, usuarios, onClick, darkMode }) {
  const formatLoginToName = (login) => {
    const clean = (login || '').trim();
    if (!clean) return '';
    const u = (usuarios || []).find((u) => u.login === clean);
    return u ? u.nome : clean;
  };
  const splitFuncionarios = (valor) => (valor || '').split('/').map((v) => v.trim());
  const getInicioNome = (valor) => formatLoginToName(splitFuncionarios(valor)[0] || '');
  const getFimNome = (valor) => formatLoginToName(splitFuncionarios(valor)[1] || '');
  const inicioNome = getInicioNome(manobra.funcionarioAtual);
  const fimNome = getFimNome(manobra.funcionarioAtual);
  const isLembrete = manobra.status === 'Lembrete';
  const isCancelada = manobra.status === 'Cancelada';
  const isFinalizada = manobra.status === 'Finalizada';

  // Lógica da Label do Estado Normal
  let estadoText = '';
  const normal = manobra.acao || 'Fechado';
  const acaoInicio = normal === 'Fechado' ? 'Fechado' : 'Aberto';
  const acaoFim = normal === 'Fechado' ? 'Aberto' : 'Fechado';

  if (isLembrete) {
    estadoText = normal === 'Fechado' ? 'Fechar' : 'Abrir';
  } else if (isFinalizada) {
    estadoText = normal === 'Fechado' ? 'Aberto' : 'Fechado';
  } else if (manobra.status === 'Em Progresso') {
    estadoText = normal === 'Fechado' ? 'Fechado' : 'Aberto';
  }

  let borderCol = 'border-l-blue-500';
  let bgCol = darkMode ? 'bg-neutral-900 hover:bg-neutral-700/80' : 'bg-white hover:bg-neutral-50';
  if (isLembrete) {
    borderCol = 'border-l-red-500';
    bgCol = darkMode ? 'bg-red-950/25 hover:bg-red-950/40' : 'bg-red-50 hover:bg-red-100/50';
  } else if (isCancelada) {
    borderCol = 'border-l-slate-400';
    bgCol = darkMode ? 'bg-neutral-900/40 text-slate-500' : 'bg-neutral-100';
  } else if (isFinalizada) {
    borderCol = 'border-l-green-500';
  }

  return (
    <div onClick={onClick} className={`border rounded-xl p-4 shadow-sm cursor-pointer transition duration-150 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${bgCol} ${darkMode ? 'border-slate-700/80' : 'border-slate-200'} border-l-4 ${borderCol}`}>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h3 className={`font-bold text-lg ${isCancelada ? 'line-through' : ''} ${darkMode ? 'text-white' : 'text-slate-800'}`}>{manobra.registro || 'Registro não informado'}{estadoText && <span className="ml-2 font-medium opacity-80">[{estadoText}]</span>}</h3>
          {manobra.prioridade === 'Sim' && (
            <span className={`text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1 ${darkMode ? 'bg-orange-950/80 text-orange-400' : 'bg-orange-100 text-orange-800'}`}><AlertTriangle size={12} /> Prioridade</span>
          )}
        </div>
        <div className={`text-sm flex items-center gap-4 flex-wrap ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
          <span className="flex items-center gap-1"><MapPin size={14} /> {manobra.local}</span>
          {manobra.atribuidoA && isLembrete && <span className="text-red-500 font-medium">Atribuído a: {formatLoginToName(manobra.atribuidoA)}</span>}
          <span className="flex items-center gap-1"><Clock size={14} /> Início: {manobra.inicio ? `${formatTimestampBR(manobra.inicio)} ${inicioNome ? `(${inicioNome}) ` : ''}- ${acaoInicio}` : '--/--/-- --:--'}</span>
          <span className="flex items-center gap-1"><Clock size={14} /> Fim: {manobra.fim ? `${formatTimestampBR(manobra.fim)} ${fimNome ? `(${fimNome}) ` : ''}- ${acaoFim}` : '--/--/-- --:--'}</span>
        </div>
      </div>
      <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center">
        <span className={`text-sm font-bold px-3 py-1 rounded-full ${isLembrete ? 'bg-red-100 text-red-700 dark:bg-red-900/55 dark:text-red-300' : isFinalizada ? 'bg-green-100 text-green-700 dark:bg-green-900/55 dark:text-green-300' : isCancelada ? 'bg-neutral-200 text-slate-600 dark:bg-neutral-700 dark:text-slate-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/55 dark:text-blue-300'}`}>{manobra.status}</span>
      </div>
    </div>
  );
}

function ManobraForm({ manobra, appUser, bairros, padroes, usuarios, onSave, onCancel, darkMode }) {
  const isEdit = !!manobra;
  const canAssign = appUser.role === ROLE_ADMIN || appUser.role === ROLE_OPERADOR;

  const splitTime = (timeStr) => {
    if (!timeStr) return { h: '', m: '' };
    const parts = timeStr.split(':');
    return { h: parts[0] || '00', m: parts[1] || '00' };
  };

  const initialTime = splitTime(manobra?.inicioTime || '');
  const initialFimTime = splitTime(manobra?.fimTime || '');

  const [formData, setFormData] = useState({
    registro: manobra?.registro || '',
    local: manobra?.local || '',
    // Atualizado de 'normal' para 'acao' para manter compatibilidade com as migrações
    acao: manobra?.acao || manobra?.normal || 'Aberto',
    vI: manobra?.vI || '',
    inicioDate: manobra?.inicioDate !== undefined ? manobra.inicioDate : '',
    inicioHour: initialTime.h,
    inicioMinute: initialTime.m,
    observacao: manobra?.observacao || '',
    prioridade: manobra?.prioridade || 'Não',
    atribuidoA: manobra?.atribuidoA || '',
    funcionarioAtual: manobra?.funcionarioAtual || appUser.login,
    fimDate: manobra?.fimDate || '',
    fimHour: initialFimTime.h,
    fimMinute: initialFimTime.m,
    fimTime: manobra?.fimTime || '',
    vF: manobra?.vF || '',
    historicoEdicao: manobra?.historicoEdicao || '',
    status: manobra?.status || ''
  });

  useEffect(() => {
    if (!appUser) return;

    const ehFuncionario = appUser.isFuncionario === true || appUser.IsFuncionario === true;
    const loginAtual = String(appUser.login || '').trim();

    setFormData((prev) => {
      if (prev.atribuidoA) return prev;
      if (ehFuncionario && loginAtual) {
        return { ...prev, atribuidoA: loginAtual };
      }
      return prev;
    });
  }, [appUser]);

  const handlePadraoSelect = (e) => {
    const p = padroes.find((x) => x.id === e.target.value);
    if (p) {
      // Calcula o oposto do status do padrão para aplicar como ação
      let acaoOposta = 'Aberto'; // Valor padrão de segurança

      if (p.status) {
        const statusFormatado = p.status.toLowerCase().trim();
        if (statusFormatado === 'aberto') {
          acaoOposta = 'Fechado';
        } else if (statusFormatado === 'fechado') {
          acaoOposta = 'Aberto';
        }
      }

      setFormData((prev) => ({
        ...prev,
        registro: p.registro,
        local: p.local,
        observacao: p.observacao,
        acao: acaoOposta // Aplica a ação invertida automaticamente
      }));
    }
  };

  const handleSetTimeNow = () => {
    const timeObj = getCurrentTimeObject();
    setFormData({ ...formData, inicioDate: getCurrentDate(), inicioHour: timeObj.hour, inicioMinute: timeObj.minute });
  };

  const handleClearTime = () => setFormData({ ...formData, inicioDate: '', inicioHour: '', inicioMinute: '' });

  const handleSetFimFormNow = () => {
    const timeObj = getCurrentTimeObject();
    setFormData({ ...formData, fimDate: getCurrentDate(), fimHour: timeObj.hour, fimMinute: timeObj.minute });
  };

  const handleClearFimForm = () => setFormData({ ...formData, fimDate: '', fimHour: '', fimMinute: '' });

  const handleSubmit = () => {
    if (!formData.atribuidoA) {
      alert('Selecione um funcionário. É obrigatório.');
      return;
    }

    // Valida se a data/hora de início não excede 1h no futuro
    if (formData.inicioDate && formData.inicioHour && formData.inicioMinute) {
      const inicioSelecionado = buildBrazilDateFromInput(formData.inicioDate, `${formData.inicioHour}:${formData.inicioMinute}`);
      if (inicioSelecionado > new Date(Date.now() + 60 * 60 * 1000)) {
        alert('A data/hora de início não pode ser mais de 1 hora no futuro.');
        return;
      }
    }

    // Valida se a data/hora de fim não excede 1h no futuro
    if (formData.fimDate && formData.fimHour && formData.fimMinute) {
      const fimSelecionado = buildBrazilDateFromInput(formData.fimDate, `${formData.fimHour}:${formData.fimMinute}`);
      if (fimSelecionado > new Date(Date.now() + 60 * 60 * 1000)) {
        alert('A data/hora de fim não pode ser mais de 1 hora no futuro.');
        return;
      }
    }

    // Valida se a data/hora de fim não é anterior à de início
    if (formData.inicioDate && formData.inicioHour && formData.inicioMinute && formData.fimDate && formData.fimHour && formData.fimMinute) {
      const inicioSelecionado = buildBrazilDateFromInput(formData.inicioDate, `${formData.inicioHour}:${formData.inicioMinute}`);
      const fimSelecionado = buildBrazilDateFromInput(formData.fimDate, `${formData.fimHour}:${formData.fimMinute}`);
      if (fimSelecionado < inicioSelecionado) {
        alert('A data/hora de fim não pode ser anterior à data/hora de início.');
        return;
      }
    }

    let payload = { ...formData };
    const timeStr = payload.inicioHour && payload.inicioMinute ? `${payload.inicioHour}:${payload.inicioMinute}` : '';
    const fimTimeStr = payload.fimHour && payload.fimMinute ? `${payload.fimHour}:${payload.fimMinute}` : '';
    delete payload.inicioHour;
    delete payload.inicioMinute;
    delete payload.fimHour;
    delete payload.fimMinute;
    payload.inicioTime = timeStr;
    payload.fimTime = fimTimeStr;

    if (!(payload.atribuidoA && !payload.inicioDate)) {
      if (!payload.inicioDate) payload.inicioDate = getCurrentDate();
      if (!payload.inicioTime) {
        const timeObj = getCurrentTimeObject();
        payload.inicioTime = `${timeObj.hour}:${timeObj.minute}`;
      }
    }

    payload.funcionarioAtual = payload.atribuidoA || payload.funcionarioAtual || '';
    if (isEdit) payload.id = manobra.id;
    onSave(payload);
  };

  return (
    <div className={`p-6 rounded-xl shadow-sm border ${darkMode ? 'bg-neutral-900 border-slate-700' : 'bg-white border-slate-200'}`}>
      <h2 className="text-xl font-bold mb-4">{isEdit ? 'Editar Manobra' : 'Nova Manobra'}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {!isEdit && padroes.length > 0 && (
          <div className={`md:col-span-2 p-3 rounded-lg border ${darkMode ? 'bg-blue-950/20 border-blue-900/60 text-blue-300' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
            <label className="block text-sm font-medium mb-1">Preencher com Manobra Padrão?</label>
            <select onChange={handlePadraoSelect} className={`w-full p-2 border rounded-lg ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-blue-200'}`}>
              <option value="">Selecione um padrão (opcional)...</option>
              {[...(padroes || [])].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })).map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Registro *</label>
          <input type="text" value={formData.registro} onChange={(e) => setFormData({ ...formData, registro: e.target.value })} className={`w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`} placeholder="Nome/Identificação" />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Local (Bairro) *</label>
          <select value={formData.local} onChange={(e) => setFormData({ ...formData, local: e.target.value })} className={`w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
            <option value="">Selecione...</option>
            {[...(bairros || [])].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })).map((b) => <option key={b.id} value={b.nome}>{b.nome}</option>)}
          </select>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Ação</label>
          <select value={formData.acao} onChange={(e) => setFormData({ ...formData, acao: e.target.value })} className={`w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
            <option value="Aberto">Abrir</option>
            <option value="Fechado">Fechar</option>
          </select>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Prioridade</label>
          <select value={formData.prioridade} onChange={(e) => setFormData({ ...formData, prioridade: e.target.value })} className={`w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
            <option value="Não">Não</option>
            <option value="Sim">Sim</option>
          </select>
        </div>

        {canAssign && !isEdit && (
          <div className={`md:col-span-2 border-t pt-4 mt-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <p className={`text-sm mb-2 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Funcionário</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Atribuir a</label>
                <select value={formData.atribuidoA} onChange={(e) => setFormData({ ...formData, atribuidoA: e.target.value })} className={`w-full p-2 border rounded-lg ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                  <option value="">Selecione um funcionário</option>
                  {(usuarios || []).filter((u) => u.isFuncionario === true || u.IsFuncionario === true).sort((a, b) => a.nome.localeCompare(b.nome)).map((u) => <option key={u.id} value={u.login}>{u.nome}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-xs mt-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Se atribuir a alguém e apagar a Data de Início, será criado um <b>Lembrete vermelho</b>.</label>
              </div>
            </div>
          </div>
        )}

        <div className={`md:col-span-2 border-t pt-4 mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Data Início</label>
            <input
              type="date"
              value={formData.inicioDate}
              max={getMaxAllowedDatetime().dateStr}
              onChange={(e) => setFormData({ ...formData, inicioDate: e.target.value })}
              className={`w-full p-2 border rounded-lg outline-none ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Hora Início (HH : MM)</label>
            <div className="flex gap-2 items-center">
              <>
                <select value={formData.inicioHour} onChange={(e) => setFormData({ ...formData, inicioHour: e.target.value })} className={`w-full p-2 border rounded-lg ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                  <option value="">HH</option>
                  {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                <span className="font-bold">:</span>
                <select value={formData.inicioMinute} onChange={(e) => setFormData({ ...formData, inicioMinute: e.target.value })} className={`w-full p-2 border rounded-lg ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                  <option value="">MM</option>
                  {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </>
              <button type="button" onClick={handleSetTimeNow} className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap ${darkMode ? 'bg-neutral-700 hover:bg-neutral-650 text-blue-400' : 'bg-neutral-200 hover:bg-neutral-300 text-slate-800'}`}>Agora</button>
              <button type="button" onClick={handleClearTime} className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap ${darkMode ? 'bg-rose-900/70 hover:bg-rose-800 text-rose-200' : 'bg-rose-100 hover:bg-rose-200 text-rose-700'}`}>Limpar</button>
            </div>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Voltas V(I)</label>
            <input type="number" value={formData.vI} onChange={(e) => setFormData({ ...formData, vI: e.target.value })} className={`w-full p-2 border rounded-lg outline-none ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`} placeholder="Ex: 5" />
          </div>
        </div>

        <div className="md:col-span-2">
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Observação</label>
          <textarea value={formData.observacao} onChange={(e) => setFormData({ ...formData, observacao: e.target.value })} className={`w-full p-2 border rounded-lg h-24 outline-none ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`} placeholder="Detalhes adicionais..." />
        </div>
      </div>

      <div className={`flex justify-end gap-3 border-t pt-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        <button onClick={onCancel} className={`px-4 py-2 rounded-lg transition ${darkMode ? 'text-slate-400 hover:bg-neutral-700' : 'text-slate-600 hover:bg-neutral-100'}`}>Cancelar</button>
        <button onClick={handleSubmit} disabled={!formData.registro || !formData.local} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition"><Save size={18} /> Salvar</button>
      </div>
    </div>
  );
}

function ManobraDetails({ manobra, appUser, usuarios, onEdit, onDelete, onSave, darkMode }) {
  const formatLoginToName = (login) => {
    const clean = (login || '').trim();
    if (!clean) return '';
    const u = (usuarios || []).find((u) => u.login === clean);
    return u ? u.nome : clean;
  };
  const splitFuncionarios = (valor) => (valor || '').split('/').map((v) => v.trim());
  const getInicioNome = (valor) => formatLoginToName(splitFuncionarios(valor)[0] || '');
  const getFimNome = (valor) => formatLoginToName(splitFuncionarios(valor)[1] || '');
  const inicioNome = getInicioNome(manobra.funcionarioAtual);
  const fimNome = getFimNome(manobra.funcionarioAtual);
  const [finalizeMode, setFinalizeMode] = useState(false);
  const [fimData, setFimData] = useState(getCurrentDate());
  const curTime = getCurrentTimeObject();
  const [fimHour, setFimHour] = useState(curTime.hour);
  const [fimMinute, setFimMinute] = useState(curTime.minute);
  const [vF, setVf] = useState('');
  const funcionariosDisponiveis = (usuarios || []).filter((u) => u.isFuncionario === true || u.IsFuncionario === true).sort((a, b) => a.nome.localeCompare(b.nome));
  const [funcionarioFinal, setFuncionarioFinal] = useState('');

  useEffect(() => {
    if (!appUser) return;

    const ehFuncionario = appUser.isFuncionario === true || appUser.IsFuncionario === true;
    const loginAtual = String(appUser.login || '').trim();
    const existeNaLista = funcionariosDisponiveis.some(
      (u) => String(u.login || '').trim() === loginAtual
    );

    setFuncionarioFinal((prev) => {
      if (prev) return prev;
      if (ehFuncionario && loginAtual && existeNaLista) return loginAtual;
      return funcionariosDisponiveis[0]?.login || '';
    });
  }, [appUser, funcionariosDisponiveis]);

  const isLembrete = manobra.status === 'Lembrete';
  const isFinalizada = manobra.status === 'Finalizada';
  const isCancelada = manobra.status === 'Cancelada';
  const isEmProgresso = manobra.status === 'Em Progresso';

  const canEdit = useMemo(() => {
    if (!appUser) return false;
    if (appUser.role === ROLE_VIEWER) return false;
    if (appUser.role === ROLE_ADMIN || appUser.role === ROLE_OPERADOR) return true;
    if (manobra.atribuidoA && manobra.atribuidoA !== appUser.login) return false;
    return true;
  }, [appUser, manobra]);

  const handleStartLembrete = () => {
    const timeObj = getCurrentTimeObject();
    const loginInicio = manobra.atribuidoA || ((appUser?.isFuncionario === true || appUser?.IsFuncionario === true) ? appUser.login : '');
    if (!loginInicio) {
      alert('Selecione um funcionário para o início.');
      return;
    }
    onSave({ ...manobra, inicioDate: getCurrentDate(), inicioTime: `${timeObj.hour}:${timeObj.minute}`, funcionarioAtual: loginInicio });
  };

  const handleSetFimNow = () => {
    const timeObj = getCurrentTimeObject();
    setFimData(getCurrentDate());
    setFimHour(timeObj.hour);
    setFimMinute(timeObj.minute);
  };

  const handleClearFim = () => {
    setFimData('');
    setFimHour('');
    setFimMinute('');
  };

  const handleFinalize = () => {
    if (!funcionarioFinal) {
      alert('Selecione um funcionário para a finalização.');
      return;
    }

    // Valida se a data/hora de fim não excede 1h no futuro
    if (fimData && fimHour && fimMinute) {
      const fimSelecionado = buildBrazilDateFromInput(fimData, `${fimHour}:${fimMinute}`);
      if (fimSelecionado > new Date(Date.now() + 60 * 60 * 1000)) {
        alert('A data/hora de fim não pode ser mais de 1 hora no futuro.');
        return;
      }
    }

    // Valida se a data/hora de fim não é anterior à data/hora de início
    if (fimData && fimHour && fimMinute && manobra.inicioDate && manobra.inicioTime) {
      const inicioSelecionado = buildBrazilDateFromInput(manobra.inicioDate, manobra.inicioTime);
      const fimSelecionado = buildBrazilDateFromInput(fimData, `${fimHour}:${fimMinute}`);
      if (fimSelecionado < inicioSelecionado) {
        alert('A data/hora de fim não pode ser anterior à data/hora de início.');
        return;
      }
    }

    const finishedTimeStr = fimHour && fimMinute ? `${fimHour}:${fimMinute}` : '';
    const inicioAtual = ((manobra.funcionarioAtual || manobra.atribuidoA || '').split(' / ')[0] || '').trim();
    const novoFuncionarioAtual = inicioAtual ? `${inicioAtual}/${funcionarioFinal}` : funcionarioFinal;
    onSave({ ...manobra, fimDate: fimData, fimTime: finishedTimeStr, vF, funcionarioAtual: novoFuncionarioAtual });
    setFinalizeMode(false);
  };

  // Lógica da Label do Estado
  let estadoText = manobra.acao || 'Fechado'; // Começa com o valor padrão
  const acaoInicio = (manobra.acao || 'Fechado') === 'Fechado' ? 'Fechado' : 'Aberto';
  const acaoFim = (manobra.acao || 'Fechado') === 'Fechado' ? 'Aberto' : 'Fechado';

  if (isLembrete) {
    estadoText = estadoText === 'Fechado' ? 'Fechar' : 'Abrir';
  } else if (isFinalizada) {
    estadoText = estadoText === 'Fechado' ? 'Aberto' : 'Fechado';
  } else if (isEmProgresso) {
    estadoText = estadoText === 'Fechado' ? 'Fechado' : 'Aberto';
  }

  return (
    <div className={`p-6 rounded-xl shadow-sm border ${darkMode ? 'bg-neutral-900 border-slate-700' : 'bg-white border-slate-200'}`}>
      <div className={`flex justify-between items-start mb-6 border-b pb-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        <div>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{manobra.registro}</h2>
          <p className={`flex items-center gap-1 mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}><MapPin size={16} /> {manobra.local}</p>
        </div>
        <span className={`px-4 py-1 rounded-full text-sm font-bold shadow-sm ${isLembrete ? 'bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-900' : isFinalizada ? 'bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/50 dark:text-green-300 dark:border-green-900' : isCancelada ? 'bg-neutral-200 text-slate-600 dark:bg-neutral-700 dark:text-slate-400' : 'bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-900'}`}>{manobra.status}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-8 text-sm">
        <div><span className={`${darkMode ? 'text-slate-400' : 'text-slate-500'} block mb-1`}>Estado Atual</span><strong className="text-lg">{estadoText}</strong></div>
        <div><span className={`${darkMode ? 'text-slate-400' : 'text-slate-500'} block mb-1`}>Prioridade</span><strong className="text-lg">{manobra.prioridade}</strong></div>

        {manobra.atribuidoA && isLembrete && (
          <div className={`sm:col-span-2 p-3 rounded-lg border ${darkMode ? 'bg-red-950/30 border-red-900/50 text-red-400' : 'bg-red-50 border-red-100 text-red-800'}`}>
            <strong>Atenção:</strong> Esta manobra está atribuída para <b>{getInicioNome(manobra.funcionarioAtual || manobra.atribuidoA || '')}</b>.
          </div>
        )}

        <div className={`sm:col-span-2 grid grid-cols-2 gap-4 p-4 rounded-lg ${darkMode ? 'bg-neutral-700/50' : 'bg-neutral-50'}`}>
          <div>
            <span className={`${darkMode ? 'text-slate-400' : 'text-slate-500'} block mb-1`}>Início</span>
            <strong className="text-base">{manobra.inicioDate ? `${formatTimestampBR(manobra.inicio)} ${inicioNome ? `(${inicioNome}) ` : ''}- ${acaoInicio}` : '--/--/-- --:--'}</strong>
            {manobra.vI && <div className={`${darkMode ? 'text-slate-400' : 'text-slate-500'} text-xs mt-1`}>Voltas V(I): {manobra.vI}</div>}
          </div>
          <div>
            <span className={`${darkMode ? 'text-slate-400' : 'text-slate-500'} block mb-1`}>Fim</span>
            <strong className="text-base">{manobra.fimDate ? `${formatTimestampBR(manobra.fim)} ${fimNome ? `(${fimNome}) ` : ''}- ${acaoFim}` : '--/--/-- --:--'}</strong>
            {manobra.vF && <div className={`${darkMode ? 'text-slate-400' : 'text-slate-500'} text-xs mt-1`}>Voltas V(F): {manobra.vF}</div>}
          </div>
        </div>

        {manobra.observacao && (
          <div className="sm:col-span-2 mt-2">
            <span className={`${darkMode ? 'text-slate-400' : 'text-slate-500'} block mb-1`}>Observação</span>
            <p className={`p-3 rounded border ${darkMode ? 'bg-amber-950/20 border-amber-900/50 text-amber-300' : 'bg-yellow-50 border-yellow-100'}`}>{manobra.observacao}</p>
          </div>
        )}
      </div>

      {finalizeMode && (
        <div className={`p-4 rounded-lg border mb-6 ${darkMode ? 'bg-emerald-950/25 border-emerald-900/60 text-emerald-300' : 'bg-green-50 border-green-200 text-green-900'}`}>
          <h3 className="font-bold text-lg mb-3">Finalizar Manobra</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm mb-1 font-medium">Data Fim</label>
              <input
                type="date"
                value={fimData}
                max={getMaxAllowedDatetime().dateStr}
                onChange={(e) => setFimData(e.target.value)}
                className={`w-full p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-650 text-white' : 'bg-white border-green-300'}`}
              />
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">Hora Fim (HH : MM)</label>
              <div className="flex gap-2 items-center">
                <>
                  <select value={fimHour} onChange={(e) => setFimHour(e.target.value)} className={`w-full p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-650 text-white' : 'bg-white border-green-300'}`}>
                    <option value="">HH</option>
                    {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="font-bold">:</span>
                  <select value={fimMinute} onChange={(e) => setFimMinute(e.target.value)} className={`w-full p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-650 text-white' : 'bg-white border-green-300'}`}>
                    <option value="">MM</option>
                    {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </>
                <button type="button" onClick={handleSetFimNow} className={`px-3 py-2 rounded text-xs font-bold transition whitespace-nowrap ${darkMode ? 'bg-emerald-800 text-white hover:bg-emerald-700' : 'bg-green-200 text-green-800 hover:bg-green-300'}`}>Agora</button>
                <button type="button" onClick={handleClearFim} className={`px-3 py-2 rounded text-xs font-bold transition whitespace-nowrap ${darkMode ? 'bg-rose-900/70 text-rose-200 hover:bg-rose-800' : 'bg-rose-100 text-rose-700 hover:bg-rose-200'}`}>Limpar</button>
              </div>
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">Voltas V(F)</label>
              <input type="number" value={vF} onChange={(e) => setVf(e.target.value)} className={`w-full p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-650 text-white' : 'bg-white border-green-300'}`} placeholder="Ex: 5" />
            </div>
            <div className="sm:col-span-3">
              <label className="block text-sm mb-1 font-medium">Funcionário que finalizou</label>
              <select value={funcionarioFinal} onChange={(e) => setFuncionarioFinal(e.target.value)} className={`w-full p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-650 text-white' : 'bg-white border-green-300'}`}>
                {funcionariosDisponiveis.map((u) => <option key={u.id} value={u.login}>{u.nome}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setFinalizeMode(false)} className={`px-4 py-2 bg-white border text-slate-700 rounded transition hover:bg-neutral-100 ${darkMode ? 'border-slate-650 bg-neutral-700 text-white hover:bg-neutral-600' : 'border-green-300'}`}>Cancelar</button>
            <button onClick={handleFinalize} className="px-4 py-2 bg-green-600 text-white font-bold rounded hover:bg-green-700 transition">Confirmar Finalização</button>
          </div>
        </div>
      )}

      <div className={`flex flex-wrap gap-3 border-t pt-4 justify-between items-center ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Última Atualização:<br />{manobra.historicoEdicao || 'Sem histórico'} </div>
        <div className="flex gap-2 flex-wrap">
          {!isCancelada && canEdit && (
            <>
              {isLembrete && <button onClick={handleStartLembrete} className={`px-4 py-2 rounded-lg font-medium transition ${darkMode ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}>Iniciar Agora</button>}
              <button onClick={onEdit} className={`px-4 py-2 border rounded-lg font-medium transition ${darkMode ? 'border-slate-600 hover:bg-neutral-700 text-slate-300' : 'border-slate-300 hover:bg-neutral-50 text-slate-700'}`}>Editar Dados</button>
              <button onClick={() => { if (window.confirm('Deseja realmente cancelar/excluir esta manobra?')) onDelete(); }} className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/35 rounded-lg border border-transparent"><Trash2 size={20} /></button>
            </>
          )}
          {!isCancelada && appUser.role !== ROLE_VIEWER && !isFinalizada && !isLembrete && !finalizeMode && <button onClick={() => setFinalizeMode(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-green-700 transition"><CheckCircle size={18} /> Finalizar</button>}
          {!canEdit && appUser.role !== ROLE_VIEWER && <span className={`text-sm font-medium px-4 py-2 rounded ${darkMode ? 'bg-red-950/20 text-red-400' : 'bg-red-50 text-red-500'}`}>Sem permissão de alteração (Atribuída a outro)</span>}
          {appUser.role === ROLE_VIEWER && <span className={`text-sm font-medium px-4 py-2 rounded ${darkMode ? 'bg-neutral-700/80 text-slate-400' : 'bg-neutral-100 text-slate-500'}`}>Modo de Visualização</span>}
        </div>
      </div>
    </div>
  );
}

function AdminPanel({ appUser, usuarios, bairros, padroes, db, appId, darkMode }) {
  const [tab, setTab] = useState('padroes');
  const isAdmin = appUser.role === ROLE_ADMIN;
  useEffect(() => { if (!isAdmin) setTab('padroes'); }, [isAdmin]);

  // Estados separados e limpos para cada entidade
  const [formPadrao, setFormPadrao] = useState({
    nome: '',
    registro: '',
    local: '',
    observacao: '',
    reservatorioAfetado: '',
    status: ''
  });

  const [formBairro, setFormBairro] = useState({
    codigo: '',
    nome: ''
  });

  const [formUsuario, setFormUsuario] = useState({
    nome: '',
    login: '',
    senha: '',
    role: ROLE_VIEWER,
    isFuncionario: false
  });

  const addItem = async (collectionName, data) => {
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', collectionName), data);

      // Limpa apenas o formulário da aba atual após o sucesso
      if (collectionName === 'padroes') {
        setFormPadrao({ nome: '', registro: '', local: '', observacao: '', reservatorioAfetado: '', status: '' });
      } else if (collectionName === 'bairros') {
        setFormBairro({ codigo: '', nome: '' });
      } else if (collectionName === 'usuarios') {
        setFormUsuario({ nome: '', login: '', senha: '', role: ROLE_VIEWER, isFuncionario: false });
      }
    } catch (e) {
      alert('Erro: ' + e.message);
    }
  };

  const deleteItem = async (collectionName, id) => {
    if (window.confirm('Remover este item definitivamente?')) {
      try {
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', collectionName, id));
      } catch (e) {
        alert('Erro: ' + e.message);
      }
    }
  };

  return (
    <div className={`p-6 rounded-xl shadow border ${darkMode ? 'bg-neutral-900 border-slate-700' : 'bg-white border-slate-200'}`}>
      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2"><Settings /> Painel Administrativo</h2>
      <div className={`flex border-b mb-6 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        <button onClick={() => setTab('padroes')} className={`px-4 py-2 font-medium border-b-2 transition-colors ${tab === 'padroes' ? 'border-blue-600 text-blue-500' : 'border-transparent text-slate-500 hover:text-slate-300'}`}>Manobras Padrão</button>
        {isAdmin && (
          <>
            <button onClick={() => setTab('bairros')} className={`px-4 py-2 font-medium border-b-2 transition-colors ${tab === 'bairros' ? 'border-blue-600 text-blue-500' : 'border-transparent text-slate-500 hover:text-slate-300'}`}>Bairros</button>
            <button onClick={() => setTab('usuarios')} className={`px-4 py-2 font-medium border-b-2 transition-colors ${tab === 'usuarios' ? 'border-blue-600 text-blue-500' : 'border-transparent text-slate-500 hover:text-slate-300'}`}>Usuários</button>
          </>
        )}
      </div>

      {/* ABA: MANOBRAS PADRÃO */}
      {tab === 'padroes' && (
        <div>
          <div className={`p-4 rounded-lg mb-6 border ${darkMode ? 'bg-neutral-700/50 border-slate-650' : 'bg-neutral-50 border-slate-200'}`}>
            <h3 className="font-bold mb-3">Adicionar Padrão</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input type="text" placeholder="Nome do Padrão" value={formPadrao.nome} onChange={(e) => setFormPadrao({ ...formPadrao, nome: e.target.value })} className={`p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`} />
              <input type="text" placeholder="Registro" value={formPadrao.registro} onChange={(e) => setFormPadrao({ ...formPadrao, registro: e.target.value })} className={`p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`} />
              <select value={formPadrao.local} onChange={(e) => setFormPadrao({ ...formPadrao, local: e.target.value })} className={`p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`}>
                <option value="">Selecione Bairro...</option>
                {[...(bairros || [])].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })).map((b) => <option key={b.id} value={b.nome}>{b.nome}</option>)}
              </select>
              <input type="text" placeholder="Reservatórios Afetados" value={formPadrao.reservatorioAfetado} onChange={(e) => setFormPadrao({ ...formPadrao, reservatorioAfetado: e.target.value })} className={`p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`} />
              <select value={formPadrao.status} onChange={(e) => setFormPadrao({ ...formPadrao, status: e.target.value })} className={`p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`}>
                <option value="">Selecione o Status Normal...</option>
                <option value="Aberto">Aberto</option>
                <option value="Fechado">Fechado</option>
              </select>
              <input type="text" placeholder="Observação" value={formPadrao.observacao} onChange={(e) => setFormPadrao({ ...formPadrao, observacao: e.target.value })} className={`p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`} />
            </div>
            <button onClick={() => addItem('padroes', formPadrao)} disabled={!formPadrao.nome || !formPadrao.registro} className="mt-3 bg-blue-600 text-white px-4 py-2 rounded font-bold disabled:opacity-50 hover:bg-blue-750 transition">Adicionar Padrão</button>
          </div>
          <ul className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
            {[...(padroes || [])].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })).map((p) => (
              <li key={p.id} className="py-3 flex justify-between items-center">
                <div>
                  <span className="font-bold">{p.nome}</span>
                  <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Registro: {p.registro} | Local: {p.local} <br />
                    Status Normal: {p.status} | Res: {p.reservatorioAfetado}
                  </div>
                </div>
                <button onClick={() => deleteItem('padroes', p.id)} className="text-red-500 p-2"><Trash2 size={18} /></button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ABA: BAIRROS */}
      {tab === 'bairros' && isAdmin && (
        <div>
          <div className="flex gap-2 mb-6">
            <input type="text" placeholder="Código" value={formBairro.codigo} onChange={(e) => setFormBairro({ ...formBairro, codigo: e.target.value })} className={`w-1/4 p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`} />
            <input type="text" placeholder="Nome do Bairro" value={formBairro.nome} onChange={(e) => setFormBairro({ ...formBairro, nome: e.target.value })} className={`flex-1 p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`} />
            <button onClick={() => addItem('bairros', formBairro)} disabled={!formBairro.nome} className="bg-blue-600 text-white px-4 py-2 rounded font-bold disabled:opacity-50 hover:bg-blue-750 transition">Adicionar</button>
          </div>
          <ul className={`divide-y border-t ${darkMode ? 'divide-slate-700 border-slate-750' : 'divide-slate-200 border-slate-200'}`}>
            {bairros.map((b) => (
              <li key={b.id} className="py-2 flex justify-between items-center">
                <span>{b.codigo && <span className="font-mono text-sm mr-2 opacity-70">[{b.codigo}]</span>} {b.nome}</span>
                <button onClick={() => deleteItem('bairros', b.id)} className="text-red-500 p-2"><Trash2 size={18} /></button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ABA: USUÁRIOS */}
      {tab === 'usuarios' && isAdmin && (
        <div>
          <div className={`p-4 rounded-lg mb-6 border ${darkMode ? 'bg-neutral-700/50 border-slate-650' : 'bg-neutral-50 border-slate-200'}`}>
            <h3 className="font-bold mb-3">Adicionar Usuário</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <input type="text" placeholder="Nome Completo" value={formUsuario.nome} onChange={(e) => setFormUsuario({ ...formUsuario, nome: e.target.value })} className={`p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`} />
              <input type="text" placeholder="Login (Sem espaços)" value={formUsuario.login} onChange={(e) => setFormUsuario({ ...formUsuario, login: e.target.value })} className={`p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`} />
              <input type="text" placeholder="Senha" value={formUsuario.senha} onChange={(e) => setFormUsuario({ ...formUsuario, senha: e.target.value })} className={`p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`} />
              <select value={formUsuario.role} onChange={(e) => setFormUsuario({ ...formUsuario, role: Number(e.target.value) })} className={`p-2 border rounded ${darkMode ? 'bg-neutral-700 border-slate-600 text-white' : 'bg-white'}`}>
                <option value={ROLE_VIEWER}>Apenas Visualizar</option>
                <option value={ROLE_MANOBREIRO}>Manobreiro</option>
                <option value={ROLE_OPERADOR}>Operador</option>
                <option value={ROLE_ADMIN}>Administrador</option>
              </select>
              <label className={`flex items-center gap-2 text-sm ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                <input type="checkbox" checked={formUsuario.isFuncionario} onChange={(e) => setFormUsuario({ ...formUsuario, isFuncionario: e.target.checked })} />
                É funcionário
              </label>
            </div>
            <button onClick={() => addItem('usuarios', formUsuario)} disabled={!formUsuario.login || !formUsuario.senha} className="bg-blue-600 text-white px-4 py-2 rounded font-bold disabled:opacity-50 hover:bg-blue-750 transition">Criar Usuário</button>
          </div>

          <ul className={`divide-y border-t ${darkMode ? 'divide-slate-700 border-slate-750' : 'divide-slate-200 border-slate-200'}`}>
            {usuarios.map((u) => (
              <li key={u.id} className="py-3 flex justify-between items-center">
                <div>
                  <span className="font-bold">{u.nome} ({u.login})</span>
                  <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Permissão: {u.role === ROLE_ADMIN ? 'Administrador' : u.role === ROLE_OPERADOR ? 'Operador' : u.role === ROLE_MANOBREIRO ? 'Manobreiro' : 'Apenas Visualizar'}</div>
                  <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Funcionário: {u.isFuncionario ? 'Sim' : 'Não'}</div>
                </div>
                {u.login !== 'admin' && <button onClick={() => deleteItem('usuarios', u.id)} className="text-red-500 p-2"><Trash2 size={18} /></button>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
